import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, Save, Upload, X, Paperclip, LinkIcon, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { TYPE_OPTIONS, YEAR_OPTIONS } from "@/constants/options";

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function OpportunityForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: "",
    description: "",
    type: "university",
    year_groups: [],
    event_date: "",
    expiry_date: "",
    location: "",
    provider: "",
    links: [],
    file_ids: [],
  });
  const [files, setFiles] = useState([]); // [{id, original_filename, ...}]
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    api
      .get(`/opportunities/${id}`)
      .then(({ data }) => {
        // Backwards-compat: if server returns only external_link, seed the list from it
        const links = (data.links && data.links.length > 0)
          ? data.links
          : (data.external_link ? [{ label: "Visit website", url: data.external_link }] : []);
        setForm({
          title: data.title || "",
          description: data.description || "",
          type: data.type || "university",
          year_groups: data.year_groups || [],
          event_date: data.event_date ? data.event_date.slice(0, 10) : "",
          expiry_date: data.expiry_date ? data.expiry_date.slice(0, 10) : "",
          location: data.location || "",
          provider: data.provider || "",
          links,
          file_ids: (data.files || []).map((f) => f.id),
        });
        setFiles(data.files || []);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const toggleYear = (y) => {
    setForm((prev) => ({
      ...prev,
      year_groups: prev.year_groups.includes(y)
        ? prev.year_groups.filter((x) => x !== y)
        : [...prev.year_groups, y],
    }));
  };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFiles((prev) => [...prev, data]);
      setForm((prev) => ({ ...prev, file_ids: [...prev.file_ids, data.id] }));
      toast.success("File uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const onFileChange = (e) => {
    const list = Array.from(e.target.files || []);
    list.forEach(uploadFile);
    e.target.value = "";
  };

  const removeFile = (fileId) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
    setForm((prev) => ({ ...prev, file_ids: prev.file_ids.filter((x) => x !== fileId) }));
  };

  const addLink = () => {
    setForm((prev) => ({ ...prev, links: [...prev.links, { label: "", url: "" }] }));
  };
  const updateLink = (idx, patch) => {
    setForm((prev) => ({
      ...prev,
      links: prev.links.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
    }));
  };
  const removeLink = (idx) => {
    setForm((prev) => ({ ...prev, links: prev.links.filter((_, i) => i !== idx) }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.year_groups.length === 0) {
      toast.error("Select at least one year group");
      return;
    }
    if (!form.expiry_date) {
      toast.error("Set an expiry date");
      return;
    }
    // Only send links that have both a URL and a label
    const cleanLinks = form.links
      .map((l) => ({ label: (l.label || "").trim() || "Visit link", url: (l.url || "").trim() }))
      .filter((l) => l.url.length > 0);
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        event_date: form.event_date || null,
        links: cleanLinks,
        // Send external_link=null so any legacy value is cleared once the migration has run
        external_link: null,
      };
      if (isEdit) {
        await api.patch(`/opportunities/${id}`, payload);
        toast.success("Opportunity updated");
      } else {
        await api.post("/opportunities", payload);
        toast.success("Opportunity created");
      }
      navigate("/staff", { replace: true, state: { refreshedAt: Date.now() } });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C8102E]" /></div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/staff" className="inline-flex items-center gap-2 text-sm font-bold mb-6 hover:underline" data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4" />
          Back to dashboard
        </Link>

        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-6">
          {isEdit ? "Edit opportunity" : "New opportunity"}
        </h1>

        <form onSubmit={onSubmit} className="space-y-6 brut-card-static p-6 sm:p-8" data-testid="opportunity-form">
          <Field label="Title">
            <input
              required
              className="brut-input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. University of Manchester Open Day"
              data-testid="form-title"
            />
          </Field>

          <Field label="Description">
            <textarea
              required
              className="brut-input min-h-[140px]"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Tell students what they need to know..."
              data-testid="form-description"
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Type">
              <select
                required
                className="brut-input"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                data-testid="form-type"
              >
                {TYPE_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </Field>

            <Field label="Provider / Organisation (optional)">
              <input
                className="brut-input"
                value={form.provider}
                onChange={(e) => setForm({ ...form, provider: e.target.value })}
                placeholder="e.g. University of X"
                data-testid="form-provider"
              />
            </Field>
          </div>

          <Field label="Year Groups (select all that apply)">
            <div className="flex flex-wrap gap-2">
              {YEAR_OPTIONS.map((y) => {
                const selected = form.year_groups.includes(y.value);
                return (
                  <button
                    type="button"
                    key={y.value}
                    onClick={() => toggleYear(y.value)}
                    className={`brut-chip ${selected ? "brut-chip-active" : "brut-chip-inactive"}`}
                    data-testid={`form-year-${y.value}`}
                  >
                    {y.label}
                  </button>
                );
              })}
            </div>
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Event date (optional)">
              <input
                type="date"
                className="brut-input"
                value={form.event_date}
                onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                data-testid="form-event-date"
              />
            </Field>
            <Field label="Disappears after (expiry date)">
              <input
                type="date"
                required
                min={todayIso()}
                className="brut-input"
                value={form.expiry_date}
                onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                data-testid="form-expiry-date"
              />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Location (optional)">
              <input
                className="brut-input"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="e.g. London / Online"
                data-testid="form-location"
              />
            </Field>
          </div>

          <Field label="External links (optional — add as many as you like)">
            {form.links.length === 0 && (
              <p className="text-xs text-zinc-500 mb-2">
                No links yet. Add sign-up pages, prospectuses, event details, etc.
              </p>
            )}
            {form.links.length > 0 && (
              <ul className="space-y-2 mb-3" data-testid="form-links-list">
                {form.links.map((l, idx) => (
                  <li key={idx} className="flex flex-col sm:flex-row gap-2 items-start sm:items-center border-2 border-black rounded-lg p-3 bg-zinc-50">
                    <LinkIcon className="h-4 w-4 flex-shrink-0 hidden sm:block" />
                    <input
                      className="brut-input flex-1 sm:max-w-[200px]"
                      placeholder="Label (e.g. Book a place)"
                      value={l.label}
                      onChange={(e) => updateLink(idx, { label: e.target.value })}
                      data-testid={`form-link-label-${idx}`}
                    />
                    <input
                      type="url"
                      className="brut-input flex-1"
                      placeholder="https://..."
                      value={l.url}
                      onChange={(e) => updateLink(idx, { url: e.target.value })}
                      data-testid={`form-link-url-${idx}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeLink(idx)}
                      className="text-zinc-700 hover:text-[#C8102E] p-2"
                      title="Remove link"
                      data-testid={`form-link-remove-${idx}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              onClick={addLink}
              className="brut-btn-secondary text-sm inline-flex"
              data-testid="form-link-add"
            >
              <Plus className="h-4 w-4" />
              Add link
            </button>
          </Field>

          <Field label="Attachments (flyers, PDFs, PowerPoint, images)">
            <label className="brut-btn-secondary cursor-pointer inline-flex" data-testid="form-upload-btn">
              <Upload className="h-4 w-4" />
              {uploading ? "Uploading..." : "Add files"}
              <input
                type="file"
                multiple
                onChange={onFileChange}
                disabled={uploading}
                className="hidden"
                accept="image/*,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                data-testid="form-file-input"
              />
            </label>
            <p className="text-xs text-zinc-500 mt-2">
              Supports images, PDFs, Word (.doc/.docx), PowerPoint (.ppt/.pptx) and Excel (.xls/.xlsx). Max 50 MB per file.
            </p>
            {files.length > 0 && (
              <ul className="mt-3 space-y-2" data-testid="form-file-list">
                {files.map((f) => (
                  <li key={f.id} className="flex items-center justify-between gap-2 border-2 border-black rounded-lg px-3 py-2 bg-zinc-50">
                    <span className="flex items-center gap-2 text-sm font-bold truncate">
                      <Paperclip className="h-4 w-4" />
                      {f.original_filename}
                    </span>
                    <button type="button" onClick={() => removeFile(f.id)} className="text-zinc-700 hover:text-black" data-testid={`form-remove-file-${f.id}`}>
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Field>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={submitting} className="brut-btn-primary flex-1" data-testid="form-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" /> {isEdit ? "Save changes" : "Create opportunity"}</>}
            </button>
            <Link to="/staff" className="brut-btn-secondary" data-testid="form-cancel">Cancel</Link>
          </div>
        </form>
      </div>
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.2em] font-bold mb-1.5">{label}</label>
      {children}
    </div>
  );
}
